import * as React from "react";
import { format } from "date-fns";
import NumberFormat from "react-number-format";
import { useHistory } from "react-router-dom";
import { DisburseSearchField } from "@inosio/payments-sdk";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Box, Card, Grid, IconButton, Snackbar, Stack, TableContainer, TablePagination, Typography } from "@mui/material";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateRange } from "@mui/lab/DateRangePicker";
import { getDisbursementHistoryDetails, selectPmDisbursementsState, selectDisbursementHistoryDetails, selectDisburseHistoryDetailsCurrentPage } from "./sagas/pmDisbursementsSlice";
import { convertCentsToDollars } from "../../shared/util/converters";
import TableHeader from "../shared/TableHeader";
import ExportDataModal, { IFileExportDetails } from "../../components/ExportDataModal";
import ExportIcon from "../../icons/ExportIcon.svg";
import SpinnerWithNoConfirmation from "../../shared/Modal/spinnerWithNoConfirmation";
import BadRequestIcon from "../../icons/Badrequesterror.svg";
import Error500 from "../../icons/505Error.svg";

const optionValues = [
  { key: DisburseSearchField.PaymentId, value: "Payment ID" },
  { key: DisburseSearchField.PayeeName, value: "Name" },
  { key: DisburseSearchField.Amount, value: "Amount" },
  { key: DisburseSearchField.Status, value: "Status" },
];

const useStyles = makeStyles({
  gridContainer: {
    background: "#FFFFFF",
    borderRadius: "24px",
  },
  dialog: {
    padding: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  dialogbox: {
    borderRadius: "20",
    borderStyle: "solid",
  },
});

const OwnerDistributions = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const routeChange = () => history.push(`/pmui/ownerdistributions`);

  const defaultStartDate = new Date();
  const defaultEndDate = new Date();
  defaultStartDate.setFullYear(defaultStartDate.getFullYear() - 1);

  const [recordStartIndex, setRecordStartIndex] = React.useState(0);
  const [rangeSize, setRangeSize] = React.useState(100);
  const [openCalendarModal, setOpenCalendarModal] = React.useState(false);
  const [dateRangeChipOpen, setDateRangeChipOpen] = React.useState(false);
  const [searchChipOpen, setSearchChipOpen] = React.useState(false);
  const [searchField, setsearchField] = React.useState("");
  const [searchValue, setSearchValue] = React.useState("");
  const searchValueInputRef = React.createRef<HTMLInputElement>();
  const [isSearchValueEmpty, setIsSearchValueEmpty] = React.useState(false);
  const [searchBluePillValue, setSearchPillValue] = React.useState("");
  const columnsSelectRef = React.createRef<HTMLSelectElement>();
  const [columnFilterSearchField, setColumnFilterSearchField] = React.useState("");
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [calendarWidgetDateRange, setcalendarWidgetDateRange] = React.useState<DateRange<Date>>([
    // Date range to hold what the calendar currently shows
    defaultStartDate,
    defaultEndDate,
  ]);
  const [transactionAmountError, setTransactionAmountError] = React.useState('');
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);

  const [apiDateRange, setapiDateRange] = React.useState<DateRange<Date>>([
    // Date range to send to the API
    defaultStartDate,
    defaultEndDate,
  ]);

  let disburseHistoryData = useSelector(selectDisbursementHistoryDetails);
  let disburseHistoryDataBook = useSelector(selectDisburseHistoryDetailsCurrentPage);
  const disbursementState = useSelector(selectPmDisbursementsState);

  //only change the start index if we need to flag to the slice to fetch
  const setSearchPageIndex = (pageNumber: number) => {
    if (!disburseHistoryDataBook[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };

  const handleClickOpen = () => setOpenCalendarModal(true);
  const searchValueChangeHandler = (e: any) => setSearchValue(e.target.value);
  const searchFieldChangeHandler = (e: any) => setsearchField(e.target.value);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  const handleCalendarSubmit = () => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setOpenCalendarModal(false);
    setapiDateRange(calendarWidgetDateRange);
    setDateRangeChipOpen(true);
  };

  const handleClose = () => {
    setOpenCalendarModal(false);
  };

  React.useEffect(() => {
    if (!dateRangeChipOpen) {
      setapiDateRange([defaultStartDate, defaultEndDate]);
    }
  }, [dateRangeChipOpen]);

  const resetTableRows = () => {
    setSearchChipOpen(false);
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(100);
    dispatch(
      getDisbursementHistoryDetails({
        EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
        StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
        RangeSize: rangeSize,
        StartIndex: 0,
        DisburseType: 'OwnerDistribution', //This is required.
      })
    );
  };

  const formatSearchAmount = () => {
    const formattedAmount = parseFloat(searchValue.replace(/,/g, "")) * 100;
    if (searchValue.match(/[a-z]/i)) {
      setTransactionAmountError('Amount should be a valid number')
      setSnackBarOpen(true)
      return false
    }
    if (searchValue === null || searchValue === "" || isNaN(formattedAmount)) {
      return 0;
    }
    return formattedAmount;
  };

  const refreshDisbursementHistory = React.useCallback(() => {
    if (searchField.length === 0 && !dateRangeChipOpen) {
      dispatch(
        getDisbursementHistoryDetails({
          EndDate: defaultEndDate.toISOString(),
          StartDate: defaultStartDate.toISOString(),
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
          DisburseType: 'OwnerDistribution', //This is required.
        })
      );
    } else if (searchField.length === 0 && dateRangeChipOpen) {
      dispatch(
        getDisbursementHistoryDetails({
          EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
          StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
          DisburseType: 'OwnerDistribution', //This is required.
        })
      );
    } else if (searchValue.length !== 0 && !dateRangeChipOpen) {
      dispatch(
        getDisbursementHistoryDetails({
          EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
          StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
          SearchField: searchField,
          SearchValue: searchField === "Amount" ? formatSearchAmount() : searchValue.trim(),
          DisburseType: 'OwnerDistribution', //This is required.
        })
      );
    } else if (searchValue.length !== 0 && dateRangeChipOpen) {
      dispatch(
        getDisbursementHistoryDetails({
          EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
          StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
          SearchField: searchField,
          SearchValue: searchField === "Amount" ? formatSearchAmount() : searchValue.trim(),
          DisburseType: 'OwnerDistribution', //This is required.
        })
      );
    } else {
      setCurrentPage(0);
      dispatch(
        getDisbursementHistoryDetails({
          EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
          StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
          RangeSize: rangeSize,
          StartIndex: 0,
          SearchField: searchField,
          SearchValue: searchField === "Amount" ? formatSearchAmount() : searchValue.trim(),
          DisburseType: 'OwnerDistribution', //This is required.
        })
      );
    }
  }, [dispatch, apiDateRange, recordStartIndex, rangeSize, dateRangeChipOpen, searchChipOpen]);

  const handleChooseColumnSearchField = (event: any) => {
    event.preventDefault();
    if (columnsSelectRef.current) {
      setColumnFilterSearchField(columnsSelectRef?.current?.value);
    }
  };

  const handleSearch = (event: any, exportDetails?: IFileExportDetails) => {
    if (event !== null) {
      event.preventDefault();
      setSearchChipOpen(true);
    }
    if (searchValueInputRef.current) {
      setSearchPillValue(searchValueInputRef?.current?.value);
    }

    // Search field not set and date range pill not open OR search field not set and date range pill open
    if ((searchField.length === 0 && !dateRangeChipOpen) || (searchField.length === 0 && dateRangeChipOpen)) {
      dispatch(
        getDisbursementHistoryDetails({
          EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
          StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
          exportAs: exportDetails?.exportAs,
          fileName: exportDetails?.fileName,
          DisburseType: 'OwnerDistribution', //This is required.
        })
      );
      // search field and value set and date range pill not open
    } else if (searchValue.length !== 0 && searchField.length !== 0 && !dateRangeChipOpen) {
      setCurrentPage(0);
      dispatch(
        getDisbursementHistoryDetails({
          RangeSize: rangeSize,
          StartIndex: 0,
          SearchField: searchField,
          SearchValue: searchField === "Amount" ? formatSearchAmount() : searchValue.trim(),
          exportAs: exportDetails?.exportAs,
          fileName: exportDetails?.fileName,
          DisburseType: 'OwnerDistribution', //This is required.
        })
      );
      // Date range pill, search field and search value all set.
    } else {
      setCurrentPage(0);
      dispatch(
        getDisbursementHistoryDetails({
          EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
          StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
          RangeSize: rangeSize,
          StartIndex: 0,
          SearchField: searchField,
          SearchValue: searchField === "Amount" ? formatSearchAmount() : searchValue.trim(),
          exportAs: exportDetails?.exportAs,
          fileName: exportDetails?.fileName,
          DisburseType: 'OwnerDistribution', //This is required.
        })
      );
    }
  };

  React.useEffect(() => {
    refreshDisbursementHistory();
  }, [dispatch, recordStartIndex, rangeSize, apiDateRange, dateRangeChipOpen]);

  React.useEffect(() => {
    if (searchChipOpen) {
      refreshDisbursementHistory();
    }
  }, [searchChipOpen]);

  React.useEffect(() => {
    setCurrentPage(0);
    if (searchValue.length === 0) {
      setIsSearchValueEmpty(true);
    } else {
      setIsSearchValueEmpty(false);
    }
  }, [searchValue, dateRangeChipOpen]);

  const toggleShowExportModal = () => {
    setShowExportModal(!showExportModal);
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ p: 2, display: "flex", flexDirection: "column", height: 'calc( 100vh - 144px )', pb: '64px' }} className={classes.gridContainer} style={{ position: "relative" }}>
        <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
          <Alert onClose={handleAlertClose} severity="error">
            {transactionAmountError}
          </Alert>
        </Snackbar>
        <React.Fragment>
          <Box sx={{ display: "flex", p: 1, pb: 2, justifyContent: "space-between", alignItems: "start", "& .MuiTextField-root": { m: 1, width: "25ch" } }}>
            <TableHeader
              dateRangeChipOpen={dateRangeChipOpen}
              calendarWidgetDateRange={apiDateRange}
              setcalendarWidgetDateRange={setcalendarWidgetDateRange}
              defaultEndDate={defaultEndDate}
              defaultStartDate={defaultStartDate}
              rangeSize={rangeSize}
              recordStartIndex={recordStartIndex}
              routeChange={routeChange}
              setDateRangeChipOpen={setDateRangeChipOpen}
              handleClickOpen={handleClickOpen}
              searchFieldChangeHandler={searchFieldChangeHandler}
              columnsSelectRef={columnsSelectRef}
              handleChooseColumnSearchField={handleChooseColumnSearchField}
              columnFilterSearchField={columnFilterSearchField}
              searchField={searchField}
              optionValues={optionValues}
              handleSearch={handleSearch}
              searchBluePillValue={searchBluePillValue}
              searchValueInputRef={searchValueInputRef}
              searchValueChangeHandler={searchValueChangeHandler}
              searchValue={searchValue}
              isSearchValueEmpty={isSearchValueEmpty}
              handleClose={handleClose}
              openCalendarModal={openCalendarModal}
              handleCalendarSubmit={handleCalendarSubmit}
              setSearchChipOpen={setSearchChipOpen}
              searchChipOpen={searchChipOpen}
              resetSearchFilter={setsearchField}
              resetSearchValue={setSearchValue}
              resetTableRows={resetTableRows}
            />
            <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: "5px" }}>
              <IconButton aria-label="export" onClick={toggleShowExportModal} >
                <img src={ExportIcon} alt="export icon" width="22px" height="22px" />
              </IconButton>
            </Box>
          </Box>
          {
            String( disbursementState.errorMessage.statusCode)[0] === '4' ?
              <Grid container direction="row" spacing={2} alignItems="center" justifyContent='center' sx={{ height: '100vh' }} >
                <Grid item display="flex" flexDirection="column" alignItems='center'>
                  <Grid item display="flex" justifyContent='center' pb={3} mt={3}>
                    <img src={BadRequestIcon} alt='400' />
                  </Grid>
                  <Grid item justifyContent='center'>
                    <Typography
                      sx={{
                        fontFamily: "Work Sans",
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '40px',
                        lineHeight: '23px',
                        color: '#2E82AF',
                        textAlign: 'center',
                        marginTop: '10px'
                      }}>
                      {
                        disbursementState.errorMessage.statusCode
                      }
                    </Typography>
                  </Grid>
                  <Grid mt={2} item justifyContent='center'>
                    <Typography
                      sx={{
                        fontFamily: "Work Sans",
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '20px',
                        lineHeight: '23px',
                        color: '#9E9E9E',
                        textAlign: 'center',
                        marginTop: '10px'
                      }}>
                      Page not found.
                    </Typography>
                  </Grid>
                  <Grid item justifyContent='center' mt={2} xs={8}>
                    <Typography
                      sx={{
                        fontFamily: "Work Sans",
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '23px',
                        color: '#9E9E9E',
                        textAlign: 'center',
                        marginTop: '10px',
                      }}>
                      {disbursementState.errorMessage.message.length === 0 ? '' : disbursementState.errorMessage.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              : String( disbursementState.errorMessage.statusCode)[0] === '5' ?
              <Grid container direction="row" spacing={2} alignItems="center" justifyContent='center' sx={{ height: '100vh' }} >
                <Grid item display="flex" flexDirection="column" alignItems='center'>
                  <Grid item display="flex" justifyContent='center' sx={{marginLeft:'5%'}} >
                    <img src={Error500} alt='500' style={{height:'35vh'}} />
                  </Grid>
                  <Grid item justifyContent='center'>
                    <Typography
                      sx={{
                        fontFamily: "Work Sans",
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '40px',
                        lineHeight: '23px',
                        color: '#2E82AF',
                        textAlign: 'center',
                        marginTop: '10px'
                      }}>
                      {
                        disbursementState.errorMessage.statusCode
                      }
                    </Typography>
                  </Grid>
                  <Grid mt={2} item justifyContent='center'>
                    <Typography
                      sx={{
                        fontFamily: "Work Sans",
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '20px',
                        lineHeight: '23px',
                        color: '#9E9E9E',
                        textAlign: 'center',
                        marginTop: '10px'
                      }}>
                      Page not found.
                    </Typography>
                  </Grid>
                  <Grid item justifyContent='center' mt={2} xs={8}>
                    <Typography
                      sx={{
                        fontFamily: "Work Sans",
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '23px',
                        color: '#9E9E9E',
                        textAlign: 'center',
                        marginTop: '10px',
                      }}>
                      {disbursementState.errorMessage.message.length === 0 ? '' : disbursementState.errorMessage.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              : disbursementState.isFetchingDisbursement ? (
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  style={{
                    height: "1100px",
                  }}
                >
                  <SpinnerWithNoConfirmation />
                </Stack>
              ) :
                <TableContainer style={{ flex: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell id="subTitle">Date</TableCell>
                        <TableCell id="subTitle">Payment ID</TableCell>
                        <TableCell id="subTitle">Name</TableCell>
                        <TableCell id="subTitle">Amount</TableCell>
                        <TableCell id="subTitle">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody style={{ left: "28px" }}>
                      {disburseHistoryDataBook?.[currentPage] &&
                        disburseHistoryDataBook?.[currentPage].Rows?.map((eachRow, i) => {
                          return (
                            <TableRow key={eachRow.TransactionUuid}>
                              <TableCell id="content">{format(new Date(eachRow?.TransactionDate), "MM/dd/yyyy")}</TableCell>
                              <TableCell id="content">{eachRow.TransactionUuid}</TableCell>
                              <TableCell id="content">{eachRow.PayeeName}</TableCell>
                              <TableCell id="content">
                                <NumberFormat
                                  thousandsGroupStyle="thousand"
                                  value={convertCentsToDollars(eachRow.Amount)}
                                  prefix="$"
                                  decimalSeparator="."
                                  displayType="text"
                                  type="text"
                                  thousandSeparator={true}
                                  allowNegative={true}
                                  decimalScale={2}
                                  fixedDecimalScale={true}
                                />
                              </TableCell>
                              <TableCell id="content">{eachRow.Status}</TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
          }
          <TablePagination component="div" rowsPerPageOptions={[50, 100, 200]} count={disburseHistoryData.TotalCount || 0} page={currentPage} onPageChange={handleChangePage} rowsPerPage={rangeSize} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ position: 'absolute', bottom: 10, right: 10 }} />
          <ExportDataModal name={"OwnerDistribution"} visible={showExportModal} onExport={(fileType, fileName) => handleSearch(null, { exportAs: fileType, fileName })} toggleShowExportModal={toggleShowExportModal} />
        </React.Fragment>
      </Card>
    </Grid>
  );
};

export default OwnerDistributions;
